import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { FileText, Plus, Trash2 } from "lucide-react";

const ChangeOrderDialog = ({
  dialog,
  setDialog,
  onAddLineItem,
  onUpdateLineItem,
  onRemoveLineItem,
  onSave,
}) => (
  <Dialog open={dialog.open} onOpenChange={(open) => !dialog.saving && setDialog(prev => ({ ...prev, open }))}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#00341d]" />
          New Change Order
        </DialogTitle>
        <DialogDescription>
          {dialog.quote?.quote_number} - {dialog.quote?.customer_name}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6 py-4">
        {/* Line Items */}
        <div>
          <Label className="text-sm font-medium mb-2 block">Additional Services</Label>
          <div className="space-y-3">
            {dialog.lineItems.map((item) => (
              <div key={item.id} className="grid grid-cols-12 gap-2 items-end bg-zinc-50 p-3 rounded-lg">
                <div className="col-span-4">
                  <Label className="text-xs text-zinc-500">Service</Label>
                  <Input
                    value={item.service_name}
                    onChange={(e) => onUpdateLineItem(item.id, "service_name", e.target.value)}
                    placeholder="Service name"
                    className="h-9"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-zinc-500">Qty</Label>
                  <Input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => onUpdateLineItem(item.id, "quantity", parseFloat(e.target.value) || 0)}
                    className="h-9"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-zinc-500">Unit Price</Label>
                  <Input
                    type="number"
                    value={item.unit_price}
                    onChange={(e) => onUpdateLineItem(item.id, "unit_price", parseFloat(e.target.value) || 0)}
                    className="h-9"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-zinc-500">Total</Label>
                  <div className="h-9 flex items-center font-medium text-[#00341d]">
                    ${item.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="col-span-2 flex justify-end">
                  {dialog.lineItems.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveLineItem(item.id)}
                      className="h-9 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={onAddLineItem} className="w-full">
              <Plus className="w-4 h-4 mr-2" /> Add Line Item
            </Button>
          </div>
        </div>

        {/* Price Adjustment */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">Price Adjustment (+/-)</Label>
            <Input
              type="number"
              value={dialog.adjustmentAmount}
              onChange={(e) => setDialog(prev => ({ ...prev, adjustmentAmount: e.target.value }))}
              placeholder="0.00"
            />
            <p className="text-xs text-zinc-500 mt-1">Use negative for discount/credit</p>
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Adjustment Description</Label>
            <Input
              value={dialog.adjustmentDescription}
              onChange={(e) => setDialog(prev => ({ ...prev, adjustmentDescription: e.target.value }))}
              placeholder="e.g., Scope reduction credit"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <Label className="text-sm font-medium mb-2 block">Notes</Label>
          <textarea
            value={dialog.notes}
            onChange={(e) => setDialog(prev => ({ ...prev, notes: e.target.value }))}
            className="w-full border rounded-md p-3 text-sm min-h-[80px]"
            placeholder="Describe the changes..."
          />
        </div>

        {/* Requires Approval */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="requires-approval"
            checked={dialog.requiresApproval}
            onChange={(e) => setDialog(prev => ({ ...prev, requiresApproval: e.target.checked }))}
            className="rounded"
          />
          <Label htmlFor="requires-approval" className="text-sm cursor-pointer">
            Require customer approval before proceeding
          </Label>
        </div>

        {/* Summary */}
        <div className="bg-[#00341d]/5 rounded-lg p-4">
          <div className="flex justify-between items-center text-lg font-bold text-[#00341d]">
            <span>Change Order Total:</span>
            <span>
              ${(
                dialog.lineItems.reduce((sum, item) => sum + (item.total || 0), 0) +
                (parseFloat(dialog.adjustmentAmount) || 0)
              ).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => setDialog(prev => ({ ...prev, open: false }))} disabled={dialog.saving}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave(false)}
            disabled={dialog.saving}
            variant="outline"
          >
            {dialog.saving ? "Saving..." : "Save Draft"}
          </Button>
          <Button
            onClick={() => onSave(true)}
            disabled={dialog.saving}
            className="bg-[#00341d] hover:bg-[#002816] text-white"
          >
            {dialog.saving ? "Saving..." : "Save & Send to Customer"}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default ChangeOrderDialog;
